import { apiFetch } from './apiClient.js';

export type ChannelType = 'whatsapp' | 'webchat' | 'messenger' | 'instagram' | 'five9';
export type ChannelStatus = 'connected' | 'disconnected' | 'error';

export interface Channel {
  id: string;
  tenantId: string;
  name: string;
  channelType: ChannelType;
  phoneId?: string;
  displayPhoneNumber?: string;
  verifiedName?: string;
  domain?: string;
  status: ChannelStatus;
  tokenSecretArn?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ChannelCreate {
  name: string;
  channelType: ChannelType;
  phoneId?: string;
  displayPhoneNumber?: string;
  verifiedName?: string;
  domain?: string;
  status?: ChannelStatus;
  tokenSecretArn?: string;
}

export type ChannelPatch = Partial<
  Pick<
    Channel,
    | 'name'
    | 'phoneId'
    | 'displayPhoneNumber'
    | 'verifiedName'
    | 'domain'
    | 'status'
    | 'tokenSecretArn'
  >
>;

/**
 * Regex local mirror of services/tenant-mgmt SECRET_ARN_REGEX. Mantener sync.
 */
export const SECRET_ARN_REGEX =
  /^arn:aws:secretsmanager:[a-z0-9-]+:\d{12}:secret:[A-Za-z0-9_/-]+(-[A-Za-z0-9]{6})?$/;

interface ListChannelsResponse {
  items: Channel[];
}

export async function listChannels(
  tenantId: string,
  opts: { includeArchived?: boolean } = {},
): Promise<Channel[]> {
  const qs = opts.includeArchived ? '?includeArchived=true' : '';
  const res = await apiFetch<ListChannelsResponse | Channel[]>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/channels${qs}`,
  );
  // Backend devuelve { items: [...] }; mantenemos compat con array suelto.
  return Array.isArray(res) ? res : res.items;
}

export async function getChannel(tenantId: string, channelId: string): Promise<Channel> {
  return apiFetch<Channel>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/channels/${encodeURIComponent(channelId)}`,
  );
}

export async function createChannel(
  tenantId: string,
  body: ChannelCreate,
): Promise<Channel> {
  return apiFetch<Channel>(`/v1/tenants/${encodeURIComponent(tenantId)}/channels`, {
    method: 'POST',
    body,
  });
}

export async function updateChannel(
  tenantId: string,
  channelId: string,
  patch: ChannelPatch,
  ifMatchVersion: number,
): Promise<Channel> {
  return apiFetch<Channel>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/channels/${encodeURIComponent(channelId)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(ifMatchVersion) },
    },
  );
}

export async function archiveChannel(tenantId: string, channelId: string): Promise<Channel> {
  return apiFetch<Channel>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/channels/${encodeURIComponent(channelId)}/archive`,
    { method: 'POST' },
  );
}
